/**
 * Month view helpers: utilization bands and grid shaping for the payload of
 * /api/scheduling/calendar/month/. Pure functions, shared by the desktop grid, the lanes,
 * the day peek and the mobile month view so every surface reads a day the same way.
 */
import { getWeekNumber, parseDateKey } from './dateUtils';

/**
 * Utilization bands. Free capacity is the actionable signal in the month view, so a quiet
 * day reads teal (room), a full day amber and an overbooked day red.
 */
export const LOAD_BANDS = {
  off: { color: '#e2e8f0', text: '#a0aec0', labelKey: 'calendar.month.band.off' },
  // A day that has gone by: what happened is shown, but in grey, because room that has
  // passed is not room the caller can book.
  past: { color: '#cbd5e0', text: '#a0aec0', labelKey: 'calendar.month.band.past' },
  empty: { color: '#cbd5e0', text: '#0d9488', labelKey: 'calendar.month.band.free' },
  free: { color: '#0d9488', text: '#0d9488', labelKey: 'calendar.month.band.free' },
  ok: { color: '#3182ce', text: '#2c5282', labelKey: 'calendar.month.band.ok' },
  full: { color: '#f59e0b', text: '#b45309', labelKey: 'calendar.month.band.full' },
  over: { color: '#ef4444', text: '#c53030', labelKey: 'calendar.month.band.over' },
};

/** Legend order: the bands a workshop can act on, quiet days left out. */
export const LEGEND_BANDS = ['free', 'ok', 'full', 'over'];

/**
 * The band of one day row of the month payload.
 */
export function loadBand(day) {
  if (!day || !(day.available_hours > 0) || day.percent === null || day.percent === undefined) {
    return { key: 'off', ...LOAD_BANDS.off };
  }
  if (day.is_past) return { key: 'past', ...LOAD_BANDS.past };
  const pct = day.percent;
  if (pct === 0) return { key: 'empty', ...LOAD_BANDS.empty };
  if (pct > 100) return { key: 'over', ...LOAD_BANDS.over };
  if (pct >= 90) return { key: 'full', ...LOAD_BANDS.full };
  if (pct >= 60) return { key: 'ok', ...LOAD_BANDS.ok };
  return { key: 'free', ...LOAD_BANDS.free };
}

/**
 * A day with nothing to show: outside the work week, nobody available, nothing booked.
 * Such cells render as quiet grey instead of a zero-load bar.
 */
export function isQuietDay(day) {
  return (
    !day.is_workday &&
    !(day.available_hours > 0) &&
    day.work_orders === 0 &&
    (day.absences || []).length === 0 &&
    !day.note &&
    !(day.pending_bookings > 0)
  );
}

/**
 * Split the 42 day rows into weeks, each tagged with its ISO week number.
 */
// The workshop's open weekdays (0 = Monday) of a month payload's work_schedule, as a Set. A
// response from before per-weekday hours has only the start..end range (wrap-around allowed).
export function workdaysOf(schedule = {}) {
  if (Array.isArray(schedule.weekdays)) return new Set(schedule.weekdays);
  const start = schedule.week_start ?? 0;
  const end = schedule.week_end ?? 4;
  const days = new Set();
  for (let index = 0; index < 7; index += 1) {
    const inWeek = start <= end ? index >= start && index <= end : index >= start || index <= end;
    if (inWeek) days.add(index);
  }
  return days;
}

export function weeksOf(days) {
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    const rows = days.slice(i, i + 7);
    weeks.push({ number: getWeekNumber(parseDateKey(rows[0].date)), days: rows });
  }
  return weeks;
}

/**
 * Format hours the way the cells do: whole hours plain, halves with one decimal, comma in
 * Finnish and Swedish.
 */
export function formatHours(hours, language = 'fi') {
  const rounded = Math.round((hours || 0) * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return language === 'en' ? text : text.replace('.', ',');
}

/**
 * Local ISO week span text for the grid, e.g. "36-40".
 */
export function weekSpanText(days) {
  if (!days || days.length === 0) return '';
  const first = getWeekNumber(parseDateKey(days[0].date));
  const last = getWeekNumber(parseDateKey(days[days.length - 1].date));
  return first === last ? String(first) : `${first}-${last}`;
}

/**
 * Whether an absence row of the month payload takes the mechanic's whole shift. Rows from
 * before the flag existed count as all day, which is what they were drawn as.
 */
export function isAllDayAbsence(absence) {
  return absence?.all_day !== false;
}

/**
 * The clock span of a partial absence ("10:00–12:00"), null for an all-day one.
 */
export function absenceSpanText(absence) {
  if (!absence || isAllDayAbsence(absence) || !absence.start || !absence.end) return null;
  return `${absence.start}–${absence.end}`;
}

/**
 * Employees of the month payload keyed by id, for absence chips and lanes.
 */
export function employeesById(monthData) {
  const map = {};
  (monthData?.employees || []).forEach((employee) => {
    map[employee.id] = employee;
  });
  return map;
}
